import React, { useEffect, useMemo, useRef, useState } from "react";
import { Ticket } from "../../types/ticket";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowDownward,
  ArrowUpward,
  AttachFile,
} from "@mui/icons-material";
import { format } from "date-fns";
import { toggleTicketVoidStatus } from "api/canpark/tickets/toggleTicketVoidStatus";
import { stateProvinceList } from "../../data/stateProvinceList";
import { useTicketTypeMeta } from "../../context/TicketTerminologyProvider";

interface Props {
  tickets: Ticket[];
  isLoadingInitial: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  onRowClick: (rowParam: any) => void;
  isAdmin: boolean;
  minHeight?: string;
  emptyMessage?: string;
}

type Order = "asc" | "desc";

type Row = Ticket & { id: number };

interface ColumnDef {
  field: string;
  label: React.ReactNode;
  width: number;
  align?: "left" | "center" | "right";
  sortable: boolean;
  // Value used for sorting + the default rendered text
  getValue?: (row: Row) => any;
  // Custom cell renderer; falls back to getValue / row[field]
  render?: (row: Row) => React.ReactNode;
}

const stateLabel = (code: any) => {
  const s = stateProvinceList.find((x) => x.code === code);
  return s ? s.label : code ?? "";
};

const compareValues = (a: any, b: any): number => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

const TicketsTable: React.FC<Props> = ({
  tickets,
  isLoadingInitial,
  isLoadingMore,
  hasMore,
  loadMore,
  onRowClick,
  isAdmin,
  minHeight,
  emptyMessage = "No records found.",
}) => {
  const getTicketTypeMeta = useTicketTypeMeta();
  const [loadingToggle, setLoadingToggle] = useState<{
    [key: string]: boolean;
  }>({});

  const [voidOverrides, setVoidOverrides] = useState<Record<string, boolean>>(
    {}
  );

  const [orderBy, setOrderBy] = useState<string>("date");
  const [order, setOrder] = useState<Order>("desc");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const rows: Row[] = useMemo(
    () => tickets.map((ticket, index) => ({ id: index + 1, ...ticket })),
    [tickets]
  );

  const displayRows: Row[] = useMemo(
    () =>
      rows.map((r) =>
        voidOverrides[r.ticketNumber] !== undefined
          ? { ...r, isVoid: voidOverrides[r.ticketNumber] }
          : r
      ),
    [rows, voidOverrides]
  );

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (!hasMore) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      { rootMargin: "300px 0px" }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [hasMore, loadMore]);

  const handleToggleVoid = async (
    ticketNumber: string,
    currentIsVoid: boolean,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    setLoadingToggle((prev) => ({ ...prev, [ticketNumber]: true }));

    try {
      await toggleTicketVoidStatus(ticketNumber);
      setVoidOverrides((prev) => ({
        ...prev,
        [ticketNumber]: !currentIsVoid,
      }));
    } catch (error) {
      console.error(
        "Error toggling void status for Ticket #" + ticketNumber + ":",
        error
      );
    } finally {
      setLoadingToggle((prev) => ({ ...prev, [ticketNumber]: false }));
    }
  };

  const columns: ColumnDef[] = [
    {
      field: "ticketNumber",
      label: "Record #",
      width: 195,
      sortable: true,
    },
    {
      field: "ticketType",
      label: "Type",
      width: 175,
      sortable: true,
      getValue: (row) => row.ticketType,
      render: (row) => {
        const meta = getTicketTypeMeta(row.ticketType);
        return (
          <>
            {row.isVoid && <Chip label="Void" sx={{ mr: "0.5rem" }} />}
            <Chip label={meta.shortLabel} color={meta.buttonColor} />
          </>
        );
      },
    },
    {
      field: "licenseNumber",
      label: "License Plate",
      width: 140,
      sortable: true,
    },
    {
      field: "licenseState",
      label: "State",
      width: 100,
      sortable: true,
      getValue: (row) => stateLabel(row.licenseState),
    },
    {
      field: "date",
      label: "Date",
      width: 300,
      sortable: true,
      getValue: (row) =>
        row.date ? new Date(row.date as any).getTime() : null,
      render: (row) =>
        row.date
          ? format(new Date(row.date as any), "hh:mm a, EEE, dd MMM yyyy")
          : "",
    },
    {
      field: "parkingZone",
      label: "Zone/Address",
      width: 200,
      sortable: true,
    },
    {
      field: "vehicleInfo",
      label: "Vehicle",
      width: 200,
      sortable: true,
    },
    {
      field: "issuedBy",
      label: "Issued By",
      width: 150,
      sortable: true,
    },
    {
      field: "comments",
      label: "Comments",
      width: 250,
      sortable: true,
      getValue: (row) => row.comments || "",
    },
    {
      field: "attachments",
      label: (
        <Tooltip title="Attachments">
          <AttachFile sx={{ fontSize: 20 }} />
        </Tooltip>
      ),
      width: 100,
      align: "center",
      sortable: true,
      getValue: (row) => row.attachments?.length || 0,
      render: (row) => {
        const count = row.attachments?.length || 0;
        return (
          <Chip
            label={count}
            color={count > 0 ? "primary" : "default"}
            variant={count > 0 ? "filled" : "outlined"}
          />
        );
      },
    },
  ];

  if (isAdmin) {
    columns.push({
      field: "toggleVoid",
      label: "Void",
      width: 150,
      sortable: false,
      render: (row) => {
        const isVoid = !!row.isVoid;
        return (
          <Button
            variant="outlined"
            color={isVoid ? "secondary" : "primary"}
            size="small"
            onClick={(e) => handleToggleVoid(row.ticketNumber, isVoid, e)}
            disabled={loadingToggle[row.ticketNumber]}
          >
            {loadingToggle[row.ticketNumber] ? (
              <CircularProgress size={20} color="inherit" />
            ) : isVoid ? (
              "Unvoid"
            ) : (
              "Void"
            )}
          </Button>
        );
      },
    });
  }

  const sortedRows = useMemo(() => {
    const col = columns.find((c) => c.field === orderBy);
    if (!col || !col.sortable) return displayRows;
    const getVal = (row: Row) =>
      col.getValue ? col.getValue(row) : (row as any)[col.field];
    const arr = [...displayRows];
    arr.sort((a, b) => {
      const cmp = compareValues(getVal(a), getVal(b));
      return order === "asc" ? cmp : -cmp;
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayRows, orderBy, order]);

  const handleSort = (field: string) => {
    if (orderBy === field) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(field);
      setOrder("asc");
    }
  };

  const totalWidth = columns.reduce((sum, c) => sum + c.width, 0);
  const sortableColumns = columns.filter((c) => c.sortable);

  const renderMobileCard = (row: Row) => {
    const meta = getTicketTypeMeta(row.ticketType);
    const attachmentCount = row.attachments ? row.attachments.length : 0;
    const dateText = row.date
      ? format(new Date(row.date as any), "hh:mm a, EEE, dd MMM yyyy")
      : "";
    const stateText = stateLabel(row.licenseState);
    const isVoid = !!row.isVoid;
    return (
      <Card
        key={row.id}
        elevation={1}
        sx={{
          opacity: isVoid ? 0.85 : 1,
          transition: (t) => t.transitions.create("box-shadow"),
          "&:hover": { boxShadow: 4 },
        }}
      >
        <CardActionArea onClick={() => onRowClick({ row })}>
          <CardContent>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}
            >
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ flexWrap: "wrap", rowGap: 0.5 }}
              >
                {isVoid && <Chip label="Void" size="small" />}
                <Chip
                  label={meta.shortLabel}
                  color={meta.buttonColor}
                  size="small"
                />
              </Stack>
              <Chip
                icon={<AttachFile sx={{ fontSize: 16 }} />}
                label={attachmentCount}
                size="small"
                color={attachmentCount > 0 ? "primary" : "default"}
                variant={attachmentCount > 0 ? "filled" : "outlined"}
              />
            </Stack>

            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ letterSpacing: "0.04em", lineHeight: 1.2 }}
            >
              {row.licenseNumber || "—"}
              {stateText && (
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  {stateText}
                </Typography>
              )}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {dateText}
            </Typography>

            <Stack spacing={0.25} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Record #{row.ticketNumber}
              </Typography>
              {row.vehicleInfo && (
                <Typography variant="body2">
                  <Box
                    component="span"
                    sx={{ color: "text.secondary", mr: 0.5 }}
                  >
                    Vehicle:
                  </Box>
                  {row.vehicleInfo}
                </Typography>
              )}
              {row.parkingZone && (
                <Typography variant="body2">
                  <Box
                    component="span"
                    sx={{ color: "text.secondary", mr: 0.5 }}
                  >
                    Zone:
                  </Box>
                  {row.parkingZone}
                </Typography>
              )}
              {row.issuedBy && (
                <Typography variant="caption" color="text.secondary">
                  Issued by {row.issuedBy}
                </Typography>
              )}
              {row.comments && (
                <Typography
                  variant="body2"
                  sx={{ mt: 0.5, fontStyle: "italic" }}
                >
                  “{row.comments}”
                </Typography>
              )}
            </Stack>
          </CardContent>
        </CardActionArea>
        {isAdmin && (
          <>
            <Divider />
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                p: 1,
              }}
            >
              <Button
                variant="outlined"
                color={isVoid ? "secondary" : "primary"}
                size="small"
                onClick={(e) =>
                  handleToggleVoid(row.ticketNumber, isVoid, e)
                }
                disabled={loadingToggle[row.ticketNumber]}
              >
                {loadingToggle[row.ticketNumber] ? (
                  <CircularProgress size={20} color="inherit" />
                ) : isVoid ? (
                  "Unvoid"
                ) : (
                  "Void"
                )}
              </Button>
            </Box>
          </>
        )}
      </Card>
    );
  };

  const mobileSortBar = (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ mb: 1.5, width: "100%" }}
    >
      <FormControl size="small" sx={{ flex: 1 }}>
        <InputLabel>Sort by</InputLabel>
        <Select
          value={orderBy}
          label="Sort by"
          onChange={(e) => setOrderBy(e.target.value as string)}
        >
          {sortableColumns.map((c) => (
            <MenuItem key={c.field} value={c.field}>
              {typeof c.label === "string" ? c.label : c.field}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Tooltip
        title={order === "asc" ? "Ascending" : "Descending"}
        arrow
      >
        <IconButton
          size="small"
          onClick={() =>
            setOrder((o) => (o === "asc" ? "desc" : "asc"))
          }
          aria-label="Toggle sort direction"
        >
          {order === "asc" ? <ArrowUpward /> : <ArrowDownward />}
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const desktopTable = (
    <Box
      sx={{
        width: "100%",
        overflowX: "auto",
      }}
    >
      <Table
        size="small"
        sx={{
          minWidth: totalWidth,
          borderCollapse: "separate",
          "& .MuiTableCell-root": {
            borderBottom: "1px solid #E0E0E0",
          },
        }}
      >
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell
                key={col.field}
                align={col.align || "left"}
                sx={{
                  width: col.width,
                  minWidth: col.width,
                  fontWeight: 500,
                  backgroundColor: "#fafafa",
                }}
                sortDirection={orderBy === col.field ? order : false}
              >
                {col.sortable ? (
                  <TableSortLabel
                    active={orderBy === col.field}
                    direction={orderBy === col.field ? order : "asc"}
                    onClick={() => handleSort(col.field)}
                  >
                    {col.label}
                  </TableSortLabel>
                ) : (
                  col.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoadingInitial && sortedRows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                align="center"
                sx={{ py: 4 }}
              >
                <CircularProgress size={24} />
              </TableCell>
            </TableRow>
          ) : (
            sortedRows.map((row) => (
              <TableRow
                key={row.id}
                hover
                onClick={() => onRowClick({ row })}
                sx={{ cursor: "pointer" }}
              >
                {columns.map((col) => {
                  const content = col.render
                    ? col.render(row)
                    : col.getValue
                    ? col.getValue(row)
                    : (row as any)[col.field];
                  return (
                    <TableCell
                      key={col.field}
                      align={col.align || "left"}
                      sx={{ width: col.width, minWidth: col.width }}
                    >
                      {content}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Box>
  );

  const mobileCardList = (
    <Box sx={{ width: "100%" }}>
      {mobileSortBar}
      {isLoadingInitial && sortedRows.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 4,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Stack spacing={1.5}>{sortedRows.map(renderMobileCard)}</Stack>
      )}
    </Box>
  );

  return (
    <Box
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        flexGrow={1}
        sx={{
          minHeight: isMobile ? undefined : minHeight || undefined,
          width: "100%",
        }}
      >
        {isMobile ? mobileCardList : desktopTable}
        <Box
          ref={sentinelRef}
          sx={{ height: 1, width: "100%" }}
          aria-hidden
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            py: 2,
            minHeight: 48,
          }}
        >
          {isLoadingMore && (
            <>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading more…
              </Typography>
            </>
          )}
          {!isLoadingInitial &&
            !isLoadingMore &&
            !hasMore &&
            sortedRows.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                End of list ({sortedRows.length} record
                {sortedRows.length === 1 ? "" : "s"})
              </Typography>
            )}
          {!isLoadingInitial && sortedRows.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {emptyMessage}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default TicketsTable;
